/**
 * Comparison Slider Frontend Interactivity
 * Handles mouse, touch, and keyboard interactions for the comparison slider
 */

(function () {
	'use strict';

	/**
	 * Initialize a single comparison slider
	 * @param {HTMLElement} slider - The slider container element
	 */
	function initSlider(slider) {
		const container = slider.querySelector('.vw-comparison-slider-container');
		const handle = slider.querySelector('.vw-comparison-slider-handle');
		const beforeImage = slider.querySelector('.vw-comparison-slider-before');
		const hint = slider.querySelector('.vw-comparison-slider-hint');

		if (!container || !handle || !beforeImage) {
			return;
		}

		let isDragging = false;

		/**
		 * Update slider position
		 * @param {number} percentage - Position as percentage (0-100)
		 */
		function updateSliderPosition(percentage) {
			// Clamp between 0 and 100
			const clampedPercentage = Math.max(0, Math.min(100, percentage));

			// Update handle position
			handle.style.left = `${clampedPercentage}%`;

			// Update clip path for before image
			beforeImage.style.clipPath = `inset(0 ${100 - clampedPercentage}% 0 0)`;

			// Update ARIA value
			handle.setAttribute('aria-valuenow', Math.round(clampedPercentage));
		}

		/**
		 * Get percentage from event
		 * @param {MouseEvent|TouchEvent} event - The event object
		 * @returns {number} Percentage position
		 */
		function getPercentageFromEvent(event) {
			const rect = container.getBoundingClientRect();
			const clientX = event.touches ? event.touches[0].clientX : event.clientX;
			const x = clientX - rect.left;
			return (x / rect.width) * 100;
		}

		/**
		 * Handle mouse down
		 * @param {MouseEvent} event - Mouse event
		 */
		function handleMouseDown(event) {
			isDragging = true;
			updateSliderPosition(getPercentageFromEvent(event));
			event.preventDefault();
		}

		/**
		 * Handle mouse move
		 * @param {MouseEvent} event - Mouse event
		 */
		function handleMouseMove(event) {
			if (!isDragging) return;
			updateSliderPosition(getPercentageFromEvent(event));
			event.preventDefault();
		}

		/**
		 * Handle mouse up
		 */
		function handleMouseUp() {
			isDragging = false;
		}

		/**
		 * Handle touch start
		 * @param {TouchEvent} event - Touch event
		 */
		function handleTouchStart(event) {
			isDragging = true;
			updateSliderPosition(getPercentageFromEvent(event));
			event.preventDefault();
		}

		/**
		 * Handle touch move
		 * @param {TouchEvent} event - Touch event
		 */
		function handleTouchMove(event) {
			if (!isDragging) return;
			updateSliderPosition(getPercentageFromEvent(event));
			event.preventDefault();
		}

		/**
		 * Handle touch end
		 */
		function handleTouchEnd() {
			isDragging = false;
		}

		/**
		 * Handle keyboard navigation
		 * @param {KeyboardEvent} event - Keyboard event
		 */
		function handleKeyDown(event) {
			const currentPosition = parseFloat(handle.style.left) || 50;
			let newPosition = currentPosition;

			switch (event.key) {
				case 'ArrowLeft':
					newPosition = currentPosition - 1;
					event.preventDefault();
					break;
				case 'ArrowRight':
					newPosition = currentPosition + 1;
					event.preventDefault();
					break;
				case 'Home':
					newPosition = 0;
					event.preventDefault();
					break;
				case 'End':
					newPosition = 100;
					event.preventDefault();
					break;
				case 'PageDown':
					newPosition = currentPosition - 10;
					event.preventDefault();
					break;
				case 'PageUp':
					newPosition = currentPosition + 10;
					event.preventDefault();
					break;
				default:
					return;
			}

			updateSliderPosition(newPosition);
		}

		/**
		 * Hide hint on first interaction
		 */
		function hideHint() {
			if (hint) {
				hint.style.opacity = '0';
			}
		}

		// Mouse events
		handle.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		// Touch events
		handle.addEventListener('touchstart', handleTouchStart, { passive: false });
		document.addEventListener('touchmove', handleTouchMove, { passive: false });
		document.addEventListener('touchend', handleTouchEnd);

		// Keyboard events
		handle.addEventListener('keydown', handleKeyDown);

		// Hide hint on first interaction
		handle.addEventListener('mousedown', hideHint, { once: true });
		handle.addEventListener('touchstart', hideHint, { once: true });
		handle.addEventListener('keydown', hideHint, { once: true });

		// Container click to jump to position
		container.addEventListener('click', function (event) {
			if (event.target !== handle && !handle.contains(event.target)) {
				updateSliderPosition(getPercentageFromEvent(event));
				hideHint();
			}
		});

		// Cleanup function
		return function cleanup() {
			handle.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			handle.removeEventListener('touchstart', handleTouchStart);
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', handleTouchEnd);
			handle.removeEventListener('keydown', handleKeyDown);
		};
	}

	/**
	 * Initialize all comparison sliders on the page
	 */
	function initAllSliders() {
		const sliders = document.querySelectorAll('.vw-comparison-slider');
		sliders.forEach(initSlider);
	}

	// Initialize on DOMContentLoaded
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initAllSliders);
	} else {
		initAllSliders();
	}

	// Re-initialize on dynamic content changes (for block editor, AJAX, etc.)
	if (typeof MutationObserver !== 'undefined') {
		const observer = new MutationObserver(function (mutations) {
			mutations.forEach(function (mutation) {
				if (mutation.addedNodes.length) {
					mutation.addedNodes.forEach(function (node) {
						if (node.nodeType === 1) {
							// Element node
							if (node.classList && node.classList.contains('vw-comparison-slider')) {
								initSlider(node);
							} else if (node.querySelectorAll) {
								const sliders = node.querySelectorAll('.vw-comparison-slider');
								sliders.forEach(initSlider);
							}
						}
					});
				}
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}
})();
